/**
 * ============================================================================
 * RiskAssessmentsTable — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: history table of all risk assessments computed for the supplier,
 *          one row per snapshot. Shows formula version, the input vector
 *          (collapsed to JSON), the computed score, the resulting category,
 *          rationale, and the signer.
 *
 * Why the inputs are stringified: each formula version uses different keys.
 *  Rather than render a wildly-variable column set, we expose the inputs
 *  in a <code> block so SMEs can audit them as-is.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierDetail.module.css';
import type { RiskAssessmentRow } from './useSupplierDetailQuery';
import type { RiskCategory } from '../SupplierWorkbench/useSuppliersQuery';

const CAT_CLASS: Record<RiskCategory, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
  LOW: styles.critLow,
};

export interface RiskAssessmentsTableProps {
  rows: RiskAssessmentRow[];
}

export function RiskAssessmentsTable({ rows }: RiskAssessmentsTableProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.detail.risk.empty', {
          defaultValue: 'No risk assessments yet.',
        })}
      </p>
    );
  }

  return (
    <table
      className={wstyles.table}
      aria-label={t('supplier.detail.risk.tableAria', {
        defaultValue: 'Risk assessment history',
      })}
    >
      <caption className={styles.srOnly}>
        {t('supplier.detail.risk.tableCaption', {
          defaultValue:
            'Each row is a risk assessment with its formula version, inputs, computed score, category, rationale, and signer.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            {t('supplier.detail.risk.columns.ts', { defaultValue: 'Assessed at' })}
          </th>
          <th scope="col">
            {t('supplier.detail.risk.columns.version', {
              defaultValue: 'Formula version',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.risk.columns.inputs', { defaultValue: 'Inputs' })}
          </th>
          <th scope="col">
            {t('supplier.detail.risk.columns.score', { defaultValue: 'Score' })}
          </th>
          <th scope="col">
            {t('supplier.detail.risk.columns.category', {
              defaultValue: 'Category',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.risk.columns.rationale', {
              defaultValue: 'Rationale',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.risk.columns.signer', { defaultValue: 'Signer' })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.assessment_id}>
            <td>
              <time dateTime={r.ts_utc}>
                {dateFmt.format(new Date(r.ts_utc))}
              </time>
            </td>
            <td>
              <code className={styles.codeChip}>{r.formula_version}</code>
            </td>
            <td>
              <details className={styles.inputDetails}>
                <summary>
                  {t('supplier.detail.risk.inspect', {
                    defaultValue: 'Inspect inputs',
                  })}
                </summary>
                <pre className={styles.inputPre}>
                  {JSON.stringify(r.inputs, null, 2)}
                </pre>
              </details>
            </td>
            <td>
              <span className={styles.riskNumber}>{r.computed_score}</span>
            </td>
            <td>
              <span
                className={`${styles.chip} ${CAT_CLASS[r.category]}`}
                aria-label={t('supplier.detail.risk.categoryAria', {
                  defaultValue: 'Category: {{c}}',
                  c: t(`supplier.risk.${r.category}`, {
                    defaultValue: r.category,
                  }),
                })}
              >
                {t(`supplier.risk.${r.category}`, { defaultValue: r.category })}
              </span>
            </td>
            <td className={styles.rationaleCell}>{r.rationale}</td>
            <td>
              {r.signer ?? (
                <span className={styles.subdued}>
                  {t('supplier.detail.risk.unsigned', { defaultValue: 'Unsigned' })}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
